import React from 'react';
import PropTypes from 'prop-types';

import {
  Container,
  Row,
  Col
} from 'reactstrap';
import {
  validatePhone,
  validateEmail,
  validateCommaSeparatedEmails
} from '../../../utils/validators';
import { toTimeString, toDate, dateTimePickerFormat } from '../../../utils/dates';
import { fetchUsers } from '../../../actions/profile/apiRequests';
import DatePicker from '../../shared/DatePicker';
import Select from '../../shared/Select';
import CountryDropdown from '../../shared/CountryDropdown';
import StateDropdown from '../../shared/StateDropdown';
import TestPurposeDropdown from '../../shared/TestPurposeDropdown';
import InfoField from '../../shared/InfoField';
import LabeledTextInput from '../../shared/LabeledTextInput';
import AsyncDropdown from '../../shared/AsyncDropdown';
import { DEFAULT_UOM, SYSTEMS_OF_MEASURE } from '../../../constants/uom';
import mitigationSystemPresentOptions from '../../../constants/mitigationSystemPresent';
import ReportRequiredLabel from '../../shared/ReportRequiredLabel';
import messages from '../../../constants/messages';

const legacyDevice = ['RS300', 'RS800', 'SN1027'];

const propTypes = {
  test: PropTypes.object,
  errors: PropTypes.object,
  editMode: PropTypes.bool,
  onFieldChange: PropTypes.func,
  onFieldValidate: PropTypes.func,
  editTester: PropTypes.bool,
  tzAbbr: PropTypes.string
};

const toYesNoFormat = (value) => {
  return value !== null ? (value ? 'Yes' : 'No') : null
}

const toTrueFalseformat = (value) => {
  return (value === 'None Selected' ? null : (value === 'Yes'));
}

const convertNoneSelectedToNull = (value) => (value === 'None Selected') ? null : value; 

const TestInfo = ({ test, tzAbbr, errors, editMode, editTester, onFieldChange, onFieldValidate }) => {
  const uom = test.unitOfMeasure || DEFAULT_UOM;
  const units = SYSTEMS_OF_MEASURE[uom];

  const isIsnActive = test.isIsn && (test.company && test.company.isIsn);
  return (
    <Container fluid className='px-0 text-left'>
      <Row>
        <Col md={6} className='mb-3'>
          <LabeledTextInput
            editMode={false}
            label='Test ID'
            value={test.deviceTestId}
          />
        </Col>
        <Col md={6} className='mb-3'>
          <LabeledTextInput
            editMode={false}
            label='Test status'
            value={test.isCompleted ? 'Complete' : 'Incomplete'}
          />
        </Col>
      </Row>

      <Row>
        <Col md={6} className='mb-3'>
          <LabeledTextInput
            editMode={false}
            label='Model'
            value={test.deviceType}
          />
        </Col>
        <Col md={6} className='mb-3'>
          <LabeledTextInput
            editMode={false}
            label='Serial'
            value={test.serialNumber}
          />
        </Col>
      </Row>

      <Row>
        <Col md={6} className='mb-3'>
          <InfoField editMode={editMode && editTester} label='Tester Name' value={test.testerName}>
            <AsyncDropdown
              placeholder={'Select Tester'}
              fetchOptions={fetchUsers}
              getLabel={option => `${option.firstName} ${option.lastName}`}
              editMode={editMode}
              label='Tester Name'
              value={test.testerId}
              onChange={({ label, value }) => {
                onFieldChange('testerName', label);
                onFieldChange('testerId', value)
              }}
            />
          </InfoField>
        </Col>
        <Col md={6} className='mb-3'>
          <LabeledTextInput editMode={false} label="Company Name" value={test.company.name} />
        </Col>
      </Row>

      <Row>
        <Col md={6} className="mb-3">
          <LabeledTextInput
            editMode={editMode}
            label="Test Ended By"
            value={test.endTesterId} 
            error={errors.endTesterId}
            invalid={errors.endTesterId}
            maxLength={100}
            onChange={value => onFieldChange('endTesterId', value)}
            onValidate={value => onFieldValidate('endTesterId', value)}
          />
        </Col>
        <Col md={6} className="mb-3">
          <LabeledTextInput
            editMode={editMode}
            label="Ender Certification"
            value={test.enderCertificationNumber} 
            error={errors.enderCertificationNumber}
            invalid={errors.enderCertificationNumber}
            maxLength={100}
            onChange={value => onFieldChange('enderCertificationNumber', value)}
            onValidate={value => onFieldValidate('enderCertificationNumber', value)}
          />
        </Col>
      </Row>
      <Row>
        <Col md={6} className='mb-3'>
          <InfoField 
            editMode={editMode && !!legacyDevice.some((legacyType) => test.deviceType===legacyType)}
            label='Start Date/Time'
            value={test.start && toTimeString(test.start, tzAbbr)}
          >
            <DatePicker
              time={true}
              placeholder='Start Date/Time'
              onChange={value => onFieldChange('start', value)}
              onValidate={value => onFieldValidate('start', value)}
              value={test.start && toDate(test.start)}
              format={dateTimePickerFormat}
            />
          </InfoField>
        </Col>
        <Col md={6} className='mb-3'>
          <InfoField 
            editMode={editMode && !!legacyDevice.some((legacyType) => test.deviceType===legacyType)}
            label='End Date/Time'
            value={test.end && toTimeString(test.end, tzAbbr)}
          >
            <DatePicker
              time={true}
              placeholder='End Date/Time'
              onChange={value => onFieldChange('end', value)}
              onValidate={value => onFieldValidate('end', value)}
              value={test.end && toDate(test.end)}
              format={dateTimePickerFormat}
            />
          </InfoField>
        </Col>
      </Row>

      <Row>
        <Col md={6} className='mb-3'>
          <LabeledTextInput
            editMode={false}
            label='Delay (hours)'
            value={test.delay || 'No-delay'}
          />
        </Col>
        <Col md={6} className='mb-3'>
          <LabeledTextInput
            editMode={false}
            label='Test Duration (hours)'
            value={test.duration.toString()}
          />
        </Col>
      </Row>

      <Row>
      <Col md={6} className='mb-3'>
          <LabeledTextInput
            editMode={ !isIsnActive && editMode }
            label={<ReportRequiredLabel>Contact Name</ReportRequiredLabel>}
            allowBlank={false}
            isnLabel={isIsnActive}
            value={test.contactName}
            error={errors.contactName && messages.validation.requiredForReport}
            invalid={errors.contactName}
            maxLength={50}
            onChange={value => onFieldChange('contactName', value)}
            onValidate={value => onFieldValidate('contactName', value)}
          />
        </Col>
        <Col md={6} className='mb-3'>
          <LabeledTextInput
            editMode={ !isIsnActive && editMode }
            label='Contact Phone'
            isnLabel={isIsnActive}
            value={test.contactPhone || ''}
            error={errors.contactPhone}
            invalid={errors.contactPhone}
            validator={validatePhone}
            validationError='Invalid phone/fax format'
            onChange={value => onFieldChange('contactPhone', value)}
            onValidate={value => onFieldValidate('contactPhone', value)}
          />
        </Col>
      </Row>

      <Row>
        <Col md={6} className='mb-3'>
          <LabeledTextInput
            editMode={ !isIsnActive && editMode }
            label={<ReportRequiredLabel>Contact Email</ReportRequiredLabel>}
            allowBlank={false}
            name='contactEmail'
            isnLabel={isIsnActive}
            value={test.contactEmail}
            error={errors.contactEmail && messages.validation.requiredForReport}
            invalid={errors.contactEmail}
            validator={validateCommaSeparatedEmails}
            validationError='Invalid email'
            onChange={value => onFieldChange('contactEmail', value)}
            onValidate={value => onFieldValidate('contactEmail', value)}
          />
        </Col>
        <Col md={6} className='mb-3'>
          <InfoField
            editMode={ !isIsnActive && editMode }
            isnLabel={isIsnActive}
            label='Country'
            value={test.country}
          >
            <CountryDropdown
              value={test.country}
              onChange={value => onFieldChange('country', value)}
            />
          </InfoField>
        </Col>
      </Row>

      <Row>
      <Col md={6} className='mb-3'>
          <LabeledTextInput
            editMode={ !isIsnActive && editMode }
            label={<ReportRequiredLabel>Address 1</ReportRequiredLabel>}
            allowBlank={false}
            isnLabel={isIsnActive}
            value={test.address1}
            error={errors.address1 && messages.validation.requiredForReport}
            invalid={errors.address1}
            maxLength={100}
            onChange={value => onFieldChange('address1', value)}
            onValidate={value => onFieldValidate('address1', value)}
          />
        </Col>
        <Col md={6} className='mb-3'>
          <LabeledTextInput
            editMode={ !isIsnActive && editMode }
            label='City'
            isnLabel={isIsnActive}
            value={test.city || ''}
            error={errors.city}
            invalid={errors.city}
            onChange={value => onFieldChange('city', value)}
            onValidate={value => onFieldValidate('city', value)}
            maxLength={50}
          />
        </Col>
      </Row>

      <Row>
      <Col md={6} className='mb-3'>
          <LabeledTextInput
            editMode={ !isIsnActive && editMode }
            label='Address 2'
            isnLabel={isIsnActive}
            value={test.address2}
            error={errors.address2}
            invalid={errors.address2}
            onChange={value => onFieldChange('address2', value)}
            onValidate={value => onFieldValidate('address2', value)}
            maxLength={100}
          />
        </Col>
        <Col md={6} className='mb-3'>
          <InfoField
            editMode={ !isIsnActive && editMode }
            isnLabel={isIsnActive}
            label='State'
            value={test.state}
          >
            <StateDropdown
              country={test.country}
              value={test.state}
              onChange={value => onFieldChange('state', value)}
            />
          </InfoField>
        </Col>
      </Row>

      <Row>
      <Col md={6} className='mb-3'>
          <LabeledTextInput
            editMode={ !isIsnActive && editMode }
            label='County'
            isnLabel={isIsnActive}
            value={test.county}
            error={errors.county}
            invalid={errors.county}
            onChange={value => onFieldChange('county', value)}
            onValidate={value => onFieldValidate('county', value)}
            maxLength={50}
          />
        </Col>
        <Col md={6} className='mb-3'>
          <LabeledTextInput
            editMode={ !isIsnActive && editMode }
            label='Postal Code'
            isnLabel={isIsnActive}
            value={test.postalCode}
            error={errors.postalCode}
            invalid={errors.postalCode}
            minLength={2}
            maxLength={12}
            onChange={value => onFieldChange('postalCode', value)}
            onValidate={value => onFieldValidate('postalCode', value)}
          />
        </Col>
      </Row>

      <Row>
        <Col md={6} className='mb-3'>
          <LabeledTextInput
            editMode={editMode}
            label='Instrument Location'
            value={test.instrumentLocation}
            error={errors.instrumentLocation}
            invalid={errors.instrumentLocation}
            onChange={value => onFieldChange('instrumentLocation', value)}
            onValidate={value => onFieldValidate('instrumentLocation', value)}
            maxLength={100}
          />
        </Col>
        <Col md={6} className='mb-3'>
          <InfoField editMode={editMode} label='Last Calibration Date' value={test.lastCalibration}>
            <DatePicker
              time={false}
              placeholder='Last Calibration Date'
              onChange={value => onFieldChange('lastCalibration', value)}
              onValidate={value => onFieldValidate('lastCalibration', value)}
              value={test.lastCalibration && new Date(test.lastCalibration)}
            />
          </InfoField>
        </Col>
      </Row>

      <Row>
        <Col md={6} className='mb-3'>
          <InfoField editMode={editMode} label='Test Purpose' value={test.testPurpose}>
            <TestPurposeDropdown
              value={test.testPurpose}
              onChange={value => onFieldChange('testPurpose', convertNoneSelectedToNull(value))}
            />
          </InfoField>
        </Col>
        <Col md={6} className='mb-3'>
          <InfoField
            editMode={editMode}
            label='Closed House Conditions?'
            value={toYesNoFormat(test.closeHouseCondition)}
          >
            <Select
              value={toYesNoFormat(test.closeHouseCondition)}
              onChange={value => onFieldChange('closeHouseCondition', toTrueFalseformat(value))}
              options={['None Selected', 'Yes', 'No']}
            />
          </InfoField>
        </Col>
      </Row>

      <Row>
        <Col md={6} className='mb-3'>
          <InfoField
            editMode={editMode}
            label='Mitigation System Present?'
            value={test.mitigationSystemPresentEnum}
          >
            <Select
              value={test.mitigationSystemPresentEnum}
              onChange={value => onFieldChange('mitigationSystemPresentEnum', convertNoneSelectedToNull(value))}
              options={mitigationSystemPresentOptions}
            />
          </InfoField>
        </Col>
        <Col md={6} className='mb-3'>
          <LabeledTextInput
            editMode={editMode}
            label={'Severe Weather Conditions'}
            error={errors.severeWeatherConditions}
            invalid={errors.severeWeatherConditions}
            value={test.severeWeatherConditions}
            onChange={value => onFieldChange('severeWeatherConditions', value)}
            onValidate={value => onFieldValidate('severeWeatherConditions', value)}
            maxLength={100}
          />
        </Col>
      </Row>

      <Row>
        <Col md={6} className="mb-3">
          <LabeledTextInput
            editMode={editMode}
            label={'Mitigation Tag Number'}
            error={errors.tagNumber}
            invalid={errors.tagNumber}
            value={test.tagNumber}
            onChange={value => onFieldChange('tagNumber', value)}
            onValidate={value => onFieldValidate('tagNumber', value)}
            maxLength={100}
          />
        </Col>
        <Col md={6} className="mb-3">
          <LabeledTextInput
            editMode={editMode}
            label={'Permanent Vents'}
            error={errors.permanentVents}
            invalid={errors.permanentVents}
            value={test.permanentVents}
            onChange={value => onFieldChange('permanentVents', value)}
            onValidate={value => onFieldValidate('permanentVents', value)}
            maxLength={100}
          />
        </Col>
      </Row>
      
      <Row>
        <Col md={6} className="mb-3">
          <LabeledTextInput
            type='number'
            editMode={editMode}
            label={`Site Relative Humidity (${units.humidity})`}
            value={test.siteRelativeHumidity}
            onChange={value => onFieldChange('siteRelativeHumidity', value)}
          />
        </Col>
        <Col md={6} className="mb-3">
          <LabeledTextInput
            type="number"
            editMode={editMode}
            label={`Site Temperature (${units.temperature})`}
            value={test.siteTemperature}
            onChange={value => onFieldChange('siteTemperature', value)}
          />
        </Col>
      </Row>

      <Row>
        <Col md={6} className='mb-3'>
          <LabeledTextInput
            editMode={editMode}
            label='Miscellaneous Fields'
            value={test.miscellaneous1}
            error={errors.miscellaneous1}
            invalid={errors.miscellaneous1}
            onChange={value => onFieldChange('miscellaneous1', value)}
            onValidate={value => onFieldValidate('miscellaneous1', value)}
            maxLength={100}
          />
        </Col>
        <Col md={6} className='mb-3 pt-2'>
          <LabeledTextInput
            editMode={editMode}
            value={test.miscellaneous2}
            error={errors.miscellaneous2}
            invalid={errors.miscellaneous2}
            onChange={value => onFieldChange('miscellaneous2', value)}
            onValidate={value => onFieldValidate('miscellaneous2', value)}
            maxLength={100}
          />
        </Col>
      </Row>

      <Row>

        <Col md={6} className='mb-3 pt-2'>
          <LabeledTextInput
            editMode={editMode}
            value={test.miscellaneous3}
            error={errors.miscellaneous3}
            invalid={errors.miscellaneous3}
            onChange={value => onFieldChange('miscellaneous3', value)}
            onValidate={value => onFieldValidate('miscellaneous3', value)}
            maxLength={100}
          />
        </Col>
        <Col md={6} className='mb-3 pt-2'>
          <LabeledTextInput
            editMode={editMode}
            value={test.miscellaneous4}
            error={errors.miscellaneous4}
            invalid={errors.miscellaneous4}
            onChange={value => onFieldChange('miscellaneous4', value)}
            onValidate={value => onFieldValidate('miscellaneous4', value)}
            maxLength={100}
          />
        </Col>
      </Row>

    </Container>
  );
};

TestInfo.propTypes = propTypes;

export default TestInfo;



// WEBPACK FOOTER //
// src/components/testDetails/TestInfo/index.jsx